import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';
// @ts-ignore
import html2canvas from 'html2canvas';
// @ts-ignore
import jsPDF from 'jspdf';

/**
 * Options for page capture operations
 */
export interface CaptureOptions {
  /** Target element or CSS selector */
  target?: HTMLElement | string;
  /** Target element (alternative to target) */
  element?: HTMLElement;
  /** Image quality (0-1) */
  quality?: number;
  /** Scale factor for higher resolution (default: 1) */
  scale?: number;
  /** Background color */
  backgroundColor?: string;
  /** Filename for download (without extension) */
  filename?: string;
  /** File format */
  format?: 'image' | 'pdf';
  /** Delay before capture in ms */
  delay?: number;
}

/**
 * Service for capturing web pages as images or PDFs.
 * Supports web platform with browser download functionality.
 * Mobile platform support to be added later with native permissions.
 */
@Injectable({ providedIn: 'root' })
export class PageCaptureService {
  constructor(private platform: Platform) {}

  /**
   * Capture a page or element as an image blob.
   * Uses toBlob() to avoid memory overflow with large elements.
   *
   * @param options - Capture options
   * @returns Promise<Blob> - Image blob in PNG format
   */
  async capturePageAsImage(options: CaptureOptions = {}): Promise<Blob> {
    try {
      const canvas = await this.captureToCanvas(options);

      // FIX #1: Use toBlob() instead of toDataURL() to avoid memory overflow
      const blob = await new Promise<Blob>(resolve => {
        canvas.toBlob(blob => resolve(blob!), 'image/png', options.quality || 1);
      });

      return blob;
    } catch (error) {
      console.error('PageCaptureService: Error capturing image:', error);
      throw error;
    }
  }

  /**
   * Capture a page or element as a PDF document.
   * Uses toBlob() for memory efficiency and fixed pagination logic.
   *
   * @param options - Capture options
   * @returns Promise<Blob> - PDF document blob
   */
  async capturePageAsPdf(options: CaptureOptions = {}): Promise<Blob> {
    try {
      const canvas = await this.captureToCanvas(options);

      // FIX #1: Use toBlob() instead of toDataURL() to avoid memory overflow
      const blob = await new Promise<Blob>(resolve => {
        canvas.toBlob(blob => resolve(blob!), 'image/png', options.quality || 1);
      });

      const imgData = await this.blobToBase64(blob);
      const imgWidth = canvas.width;
      const imgHeight = canvas.height;

      // A4 dimensions in mm
      const pdfWidth = 210;
      const pdfHeight = 297;
      const margin = 10;

      const maxPageWidth = pdfWidth - 2 * margin;
      const maxPageHeight = pdfHeight - 2 * margin;

      // Calculate scaling to fit page width
      const scaleFactor = maxPageWidth / imgWidth;
      const scaledWidth = imgWidth * scaleFactor;
      const scaledHeight = imgHeight * scaleFactor;

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageHeight = pdfHeight - 2 * margin;
      // Add margin-top for next pages to prevent truncation
      const pageMarginTop = 10; // 10mm margin for next pages

      let heightLeft = scaledHeight;
      let position = margin;

      // FIX #2: Changed from >= 0 to > 0 to prevent extra blank page
      while (heightLeft > 0) {
        pdf.addImage(imgData, 'PNG', margin, position, scaledWidth, scaledHeight);

        heightLeft -= pageHeight;

        // Add new page only if more content remains
        if (heightLeft > 0) {
          position -= pageHeight + pageMarginTop;
          pdf.addPage();
        }
      }

      const pdfBlob = await new Promise<Blob>(resolve => {
        resolve(pdf.output('blob'));
      });

      return pdfBlob;
    } catch (error) {
      console.error('PageCaptureService: Error capturing PDF:', error);
      throw error;
    }
  }

  /**
   * Capture an element to a canvas using html2canvas.
   * Handles full-page scrollable content capture with scroll restoration.
   *
   * @param options - Capture options
   * @returns Promise<HTMLCanvasElement> - Captured canvas
   */
  private async captureToCanvas(options: CaptureOptions = {}): Promise<HTMLCanvasElement> {
    // Get target element
    let targetElement: HTMLElement;

    if (options.element instanceof HTMLElement) {
      targetElement = options.element;
    } else if (typeof options.element === 'string') {
      const element = document.querySelector(options.element);
      if (!element) {
        throw new Error(`Element not found: ${options.element}`);
      }
      targetElement = element as HTMLElement;
    } else {
      targetElement = document.body;
    }

    // Store original scroll position and styles
    const originalScrollTop = window.scrollY;
    const originalScrollLeft = window.scrollX;

    // Store original element styles
    const originalHeight = targetElement.style.height;
    const originalOverflow = targetElement.style.overflow;
    const originalOverflowY = targetElement.style.overflowY;

    try {
      // Add delay if specified (for animations/lazy loading)
      if (options.delay) {
        await this.sleep(options.delay);
      }

      // Get full scrollable dimensions
      const fullHeight = targetElement.scrollHeight;
      const fullWidth = targetElement.scrollWidth;

      // Temporarily adjust for full-page capture
      targetElement.style.height = `${fullHeight}px`;
      targetElement.style.overflow = 'visible';
      targetElement.style.overflowY = 'visible';

      // Capture with html2canvas using full dimensions
      const canvas = await html2canvas(targetElement, {
        allowTaint: true,
        useCORS: true,
        backgroundColor: options.backgroundColor || '#ffffff',
        scale: options.scale || (this.platform.is('mobile') ? 1 : 2), // Higher quality on desktop
        logging: false,
        windowHeight: fullHeight, // CRITICAL: Capture full height
        windowWidth: fullWidth, // Capture full width
        scrollX: 0,
        scrollY: 0,
      });

      // Restore original styles
      targetElement.style.height = originalHeight;
      targetElement.style.overflow = originalOverflow;
      targetElement.style.overflowY = originalOverflowY;

      // Restore scroll position
      window.scrollTo(originalScrollLeft, originalScrollTop);

      return canvas;
    } catch (error) {
      // Ensure scroll position and styles are restored on error
      window.scrollTo(originalScrollLeft, originalScrollTop);
      targetElement.style.height = originalHeight;
      targetElement.style.overflow = originalOverflow;
      targetElement.style.overflowY = originalOverflowY;
      throw error;
    }
  }

  /**
   * Save a blob as a file on the web platform.
   * Creates a temporary download link and triggers download.
   *
   * @param blob - Content to save
   * @param filename - Download filename
   * @param mimeType - MIME type (e.g., 'image/png', 'application/pdf')
   */
  async saveOnWeb(blob: Blob, filename: string, mimeType: string): Promise<void> {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }

  /**
   * Save a blob as a file on mobile platforms (Android/iOS).
   * Uses Capacitor Filesystem API to write to device storage.
   *
   * @param blob - Content to save
   * @param filename - Download filename
   * @param mimeType - MIME type (e.g., 'image/png', 'application/pdf')
   */
  async saveOnMobile(blob: Blob, filename: string, mimeType: string): Promise<void> {
    try {
      console.log('PageCaptureService[0]: Saving file to mobile device', filename, mimeType);
      console.log('PageCaptureService[0.1]: Blob validation', {
        size: blob.size,
        type: blob.type,
        isBlob: blob instanceof Blob,
        hasBlobMethods: typeof blob.arrayBuffer === 'function' && typeof blob.text === 'function',
      });

      // Convert blob to base64 for Filesystem API
      console.log('PageCaptureService[0.2]: Starting blobToBase64 conversion...');
      const base64Data = await this.blobToBase64(blob);
      console.log(
        'PageCaptureService [11]: Saving file base64Data completed, length:',
        base64Data?.length
      );
      const data = base64Data.split(',')[1]; // Remove "data:xxx;base64," prefix
      console.log('PageCaptureService [111]: Saving file data', base64Data.split(',')[1]);

      // Sanitize filename to avoid filesystem issues
      const sanitizedFilename = this.sanitizeFilename(filename);
      console.log('PageCaptureService [1111]: Saving file sanitized filename', sanitizedFilename);

      // Use Documents directory for persistent storage
      // On Android (API 29+): No permissions needed, scoped storage
      // On iOS: App-scoped Documents directory
      const result = await Filesystem.writeFile({
        path: sanitizedFilename,
        data: data,
        directory: Directory.External,
        recursive: true,
      });

      console.log(
        'PageCaptureService [1]: Saving file to mobile device',
        base64Data,
        data,
        sanitizedFilename
      );
      console.log('PageCaptureService: File saved successfully', { result });

      // Get file URI for sharing
      const fileUri = await Filesystem.getUri({
        path: sanitizedFilename,
        directory: Directory.External,
      });

      console.log('PageCaptureService: File URI obtained', fileUri);

      // Optional: Trigger share sheet
      try {
        await Share.share({
          title: sanitizedFilename,
          url: fileUri.uri,
          dialogTitle: sanitizedFilename,
        });
        console.log('PageCaptureService: Share sheet opened successfully');
      } catch (shareError) {
        // Share failed, but file was saved - log and continue
        console.warn('PageCaptureService: Share sheet failed (file was saved)', shareError);
      }
    } catch (error) {
      console.error('PageCaptureService: Error saving file on mobile', error);
      throw new Error(
        `Failed to save file on mobile: ${error instanceof Error ? error.message : 'Unknown error'}`
      );
    }
  }

  /**
   * Sanitize filename to remove invalid characters.
   *
   * @param filename - Original filename
   * @returns Sanitized filename
   */
  private sanitizeFilename(filename: string): string {
    // Remove or replace invalid filesystem characters
    return filename
      .replace(/[<>:"/\\|?*]/g, '_') // Replace invalid chars with underscore
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 255); // Limit length (filesystem max)
  }

  /**
   * Save or share a captured file based on platform.
   * Supports web platform (browser download) and mobile platforms (native file save).
   *
   * @param blob - Content to save/share
   * @param filename - Download filename (without extension)
   * @param format - File format ('image' or 'pdf')
   */
  async saveAndShare(
    blob: Blob,
    filename: string,
    format: 'image' | 'pdf' | string
  ): Promise<void> {
    const extension = format === 'image' ? 'png' : 'pdf';
    const fullFilename = `${filename}.${extension}`;
    const mimeType = format === 'image' ? 'image/png' : 'application/pdf';

    // Detect platform and use appropriate save method
    const isMobile = this.platform.is('capacitor');
    // Capacitor.isNativePlatform() || this.platform.is('android') || this.platform.is('ios');
    const isWeb = !this.platform.is('capacitor'); // this.platform.is('mobileweb') || this.platform.is('desktop');

    console.warn(
      'PageCaptureService: save to which platform : ',
      isWeb,
      isMobile,
      Capacitor.isNativePlatform(),
      this.platform.is('capacitor'),
      fullFilename,
      filename,
      extension,
      format
    );
    if (!isMobile) {
      // Web platform: Use browser download method
      await this.saveOnWeb(blob, fullFilename, mimeType);
    } else if (isMobile) {
      // Mobile platform: Use native file system API
      await this.saveOnMobile(blob, fullFilename, mimeType);
    } else {
      // Fallback: Try web save
      console.warn('PageCaptureService: Unknown platform, falling back to web save');
      await this.saveOnWeb(blob, fullFilename, mimeType);
    }
  }

  /**
   * Convert a Blob to base64 string.
   * Used for PDF generation and future mobile functionality.
   *
   * @param blob - Blob to convert
   * @returns Promise<string> - Base64 encoded string
   */
  private async blobToBase64(blob: Blob): Promise<string> {
    console.log('PageCaptureService[blobToBase64]: Starting conversion, blob size:', blob.size);

    // On Capacitor (mobile), use blob.arrayBuffer() + manual base64 encoding
    // because FileReader event handlers don't fire properly in Capacitor WebView
    if (Capacitor.isNativePlatform()) {
      console.log(
        'PageCaptureService[blobToBase64]: Using Capacitor-compatible method (arrayBuffer + base64)'
      );
      try {
        // Get ArrayBuffer from blob
        const arrayBuffer = await blob.arrayBuffer();
        console.log(
          'PageCaptureService[blobToBase64]: Got ArrayBuffer, byteLength:',
          arrayBuffer.byteLength
        );

        // Convert ArrayBuffer to base64
        const base64 = this.arrayBufferToBase64(arrayBuffer);
        console.log(
          'PageCaptureService[blobToBase64]: Base64 conversion complete, length:',
          base64.length
        );

        // Add data URL prefix
        const mimeType = blob.type || 'application/octet-stream';
        const dataUrl = `data:${mimeType};base64,${base64}`;
        console.log('PageCaptureService[blobToBase64]: Complete data URL length:', dataUrl.length);

        return dataUrl;
      } catch (error) {
        console.error(
          'PageCaptureService[blobToBase64]: Capacitor-compatible method failed:',
          error
        );
        throw error;
      }
    }

    // On web platform, use FileReader (should work fine in browsers)
    console.log('PageCaptureService[blobToBase64]: Using FileReader (web platform)');
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      console.log('PageCaptureService[blobToBase64]: FileReader created');

      const timeoutId = setTimeout(() => {
        console.error(
          'PageCaptureService[blobToBase64]: TIMEOUT - FileReader took too long (>30s)'
        );
        reject(new Error('FileReader timeout after 30 seconds'));
      }, 30000);

      reader.onloadend = () => {
        clearTimeout(timeoutId);
        console.log(
          'PageCaptureService[blobToBase64]: onloadend fired, result type:',
          typeof reader.result
        );
        if (reader.result) {
          console.log(
            'PageCaptureService[blobToBase64]: Result length:',
            (reader.result as string).length
          );
          resolve(reader.result as string);
        } else {
          console.error(
            'PageCaptureService[blobToBase64]: onloadend fired but result is null/undefined'
          );
          reject(new Error('FileReader result is null'));
        }
      };

      reader.onerror = error => {
        clearTimeout(timeoutId);
        console.error('PageCaptureService[blobToBase64]: FileReader onerror:', error);
        console.error('PageCaptureService[blobToBase64]: FileReader error details:', {
          error,
          readyState: reader.readyState,
          result: reader.result,
        });
        reject(error);
      };

      reader.onabort = () => {
        clearTimeout(timeoutId);
        console.error('PageCaptureService[blobToBase64]: FileReader onabort fired');
        reject(new Error('FileReader operation aborted'));
      };

      console.log('PageCaptureService[blobToBase64]: Calling readAsDataURL...');
      try {
        reader.readAsDataURL(blob);
        console.log('PageCaptureService[blobToBase64]: readAsDataURL called successfully');
      } catch (syncError) {
        clearTimeout(timeoutId);
        console.error(
          'PageCaptureService[blobToBase64]: Synchronous error during readAsDataURL:',
          syncError
        );
        reject(syncError);
      }
    });
  }

  /**
   * Convert ArrayBuffer to base64 string.
   * Handles binary data properly for Capacitor compatibility.
   *
   * @param buffer - ArrayBuffer to convert
   * @returns Base64 encoded string
   */
  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    console.log('PageCaptureService[arrayBufferToBase64]: Converting ArrayBuffer to base64');
    const bytes = new Uint8Array(buffer);
    let binary = '';
    const len = bytes.byteLength;

    // Process bytes in chunks to avoid call stack issues
    for (let i = 0; i < len; i++) {
      binary += String.fromCharCode(bytes[i]);
    }

    console.log(
      'PageCaptureService[arrayBufferToBase64]: Binary string created, length:',
      binary.length
    );
    const base64 = btoa(binary);
    console.log(
      'PageCaptureService[arrayBufferToBase64]: Base64 conversion complete, length:',
      base64.length
    );

    return base64;
  }

  /**
   * Sleep utility for async operations.
   *
   * @param ms - Milliseconds to sleep
   * @returns Promise that resolves after delay
   */
  private sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }
}
